import axios, { AxiosRequestConfig } from 'axios';
import {
    getAccessTokenFromLocalStorage,
    getRefreshTokenFromLocalStorage,
    setAccessTokenFromLocalStorage,
    setRefreshTokenFromLocalStorage,
} from '../providers/DonorProvider/donorLocalStorage';
import { webConfig } from '../utils/webConfig';
import { logError } from '../utils/logError';
import applyCaseMiddleware from 'axios-case-converter';
import { awaitFingerprint, getFingerprintSync } from './fingerprint';

export type ById<T = any> = { [id: number]: T | undefined };
export const userAgent = window.navigator.userAgent;
export const appName = window.navigator.appName || userAgent;

type AxiosRequestConfigExtended = AxiosRequestConfig & {
    _retry?: boolean;
    headers: any;
};

export const axiosClient = applyCaseMiddleware(
    axios.create({
        baseURL: webConfig.baseAPIUrl,
    }),
);

export const externalAxiosClient = applyCaseMiddleware(axios.create());

const getAccessToken = (): string | undefined => {
    const accessToken = getAccessTokenFromLocalStorage();

    return accessToken && accessToken !== 'undefined'
        ? `Bearer ${accessToken}`
        : undefined;
};

const getAuthorizationHeader = (
    config: AxiosRequestConfigExtended,
): string | undefined => {
    const token = getAccessToken();
    const clientAccessToken = config.headers?.['clientAccessToken'];
    if (
        clientAccessToken &&
        clientAccessToken !== 'undefined' &&
        clientAccessToken !== 'Bearer undefined'
    ) {
        return clientAccessToken;
    }
    if (token && token !== 'undefined' && token !== 'Bearer undefined') {
        return token;
    }
    return undefined;
};

const logRefreshTokenError = (
    originalRequest: AxiosRequestConfigExtended,
    err?: Error,
    event?: string,
    response?: object,
) => {
    const refreshToken = getRefreshTokenFromLocalStorage();
    const eventName = `Unable to refresh the token in '${event}'.`;
    logError(eventName, {
        event,
        exception: err,
        originalRequest,
        refreshToken,
        response,
    });
};

let refreshPromise: Promise<string> | null = null;

axiosClient.interceptors.request.use(async (config) => {
    if (!config.headers['_forceTokenRefresh']) {
        config.headers.Authorization = getAuthorizationHeader(config);
    } else {
        config.headers.Authorization = getAccessToken();
        delete config.headers['_forceTokenRefresh'];
    }
    config.headers['Device-Name'] = config.headers['Device-Name'] || appName;
    config.headers['App-Install-Id'] =
        config.headers['App-Install-Id'] || userAgent;
    config.headers['App-Version'] = webConfig.appVersion;
    config.headers['X-REQUEST-UUID'] = webConfig.getCorrelationId();

    const fp =
        getFingerprintSync() ??
        (await awaitFingerprint(1500).catch(() => null));
    if (fp?.visitorId) {
        config.headers['X-Client-Fingerprint'] = fp.visitorId;
        if (fp.requestId) {
            config.headers['X-Client-Fingerprint-Request-Id'] = fp.requestId;
        }
    }
    return config;
});

axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            if (!refreshPromise) {
                refreshPromise = (async () => {
                    try {
                        const refreshToken = getRefreshTokenFromLocalStorage();
                        if (!refreshToken) {
                            logRefreshTokenError(
                                originalRequest,
                                undefined,
                                'No refresh token found.',
                            );
                            return Promise.reject(error);
                        }
                        const res = await axios.post(
                            new URL(
                                webConfig.refreshTokenBaseUrl,
                                webConfig.baseAPIUrl,
                            ).toString(),
                            {
                                refresh_token: refreshToken,
                            },
                            {
                                headers: {
                                    Authorization: '-',
                                    'Device-Name': appName,
                                    'App-Install-Id': userAgent,
                                },
                            },
                        );
                        if (
                            res.status === 401 ||
                            res.data.response.code === 401
                        ) {
                            logRefreshTokenError(
                                originalRequest,
                                undefined,
                                'Refresh token rejected.',
                                res,
                            );
                            return Promise.reject(error);
                        }
                        const {
                            access_token: newAccessToken,
                            refresh_token: newRefreshToken,
                        } = res.data.response.payload;

                        setRefreshTokenFromLocalStorage(newRefreshToken);
                        setAccessTokenFromLocalStorage(newAccessToken);

                        axiosClient.defaults.headers.common['Authorization'] =
                            `Bearer ${newAccessToken}`;
                        return newAccessToken;
                    } finally {
                        refreshPromise = null;
                    }
                })();
            }

            try {
                const accessToken = await refreshPromise;
                originalRequest.headers.set(
                    'Authorization',
                    `Bearer ${accessToken}`,
                );
                originalRequest.headers.delete('clientAccessToken');
                originalRequest.headers.delete('authorization');
                originalRequest.headers.set('_forceTokenRefresh', true);
                return axiosClient({
                    ...originalRequest,
                    _retry: true,
                });
            } catch (refreshError) {
                logRefreshTokenError(
                    originalRequest,
                    refreshError as any,
                    'Exception occurred when refreshing.',
                );
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    },
);
